import { Box, Grid, Typography } from '@mui/material';
import React from 'react';
import { SortColumn, SortDirection } from './ClipGrid';

interface ClipGridHeaderProps {
  sortColumn: SortColumn;
  sortDirection: SortDirection;
  onSortChange(column: SortColumn): void;
  variant?: 'default' | 'workbench';
}

// ----------------------------------------------------------------------
// 1. Custom SVGs matching the exact design in the screenshot
// ----------------------------------------------------------------------
const SortIconInactive = (): React.ReactElement => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#111"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ opacity: 0.8 }}
  >
    <circle cx="12" cy="12" r="10"></circle>
    <polyline points="16 13 12 9 8 13"></polyline>
  </svg>
);

const SortIconAsc = (): React.ReactElement => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#FACC15"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="10"></circle>
    <polyline points="16 13 12 9 8 13"></polyline>
  </svg>
);

const SortIconDesc = (): React.ReactElement => {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="#FACC15"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="10"></circle>
      <polyline points="8 11 12 15 16 11"></polyline>
    </svg>
  );
};

// ----------------------------------------------------------------------
// 2. Reusable Header Component for precise alignment and coloring
// ----------------------------------------------------------------------
type SortColumnClickHandler = (col: SortColumn) => void;

interface SortableHeaderProps {
  column: SortColumn;
  label: string;
  currentSortColumn: SortColumn;
  currentSortDirection: SortDirection;
  onClick: SortColumnClickHandler;
  align?: 'flex-start' | 'center' | 'flex-end';
  isMono?: boolean;
}

const SortableHeader: React.FC<SortableHeaderProps> = ({
  column,
  label,
  currentSortColumn,
  currentSortDirection,
  onClick,
  align = 'flex-start',
  isMono = false,
}) => {
  const isActive = currentSortColumn === column;
  const activeColor = '#FACC15'; // The yellow accent from your screenshot
  const inactiveColor = '#333';

  return (
    <Box
      onClick={() => onClick(column)}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: align,
        gap: '4px',
        cursor: 'pointer',
        userSelect: 'none',
        '&:hover': { opacity: 0.7 },
        transition: 'opacity 0.2s ease',
      }}
    >
      <Typography
        sx={{
          fontFamily: isMono ? "'Geist Mono', monospace" : 'inherit',
          fontSize: '12px',
          fontWeight: 500,
          color: isActive ? activeColor : inactiveColor,
        }}
      >
        {label}
      </Typography>
      {isActive ? (
        currentSortDirection === 'asc' ? (
          <SortIconAsc />
        ) : (
          <SortIconDesc />
        )
      ) : (
        <SortIconInactive />
      )}
    </Box>
  );
};

/**
 * Renders the header row for the clip grid with sortable columns
 */
const ClipGridHeader: React.FC<ClipGridHeaderProps> = ({
  sortColumn,
  sortDirection,
  onSortChange,
  variant = 'default',
}) => {
  const isWorkbench = variant === 'workbench';

  return (
    <Grid
      container
      spacing={isWorkbench ? 0 : { xs: 1, sm: 1.5, md: 2 }}
      sx={{
        width: '100%',
        minWidth: 0,
        maxWidth: '100%',
        margin: isWorkbench ? '0 2px 4px 2px' : '0 2px .5rem 2px',
        padding: isWorkbench ? '4px 6px' : { xs: '.25rem', sm: '.375rem', md: '.5rem' },
        alignItems: 'center',
        flexWrap: isWorkbench ? 'nowrap' : { xs: 'wrap', md: 'nowrap' },
        boxSizing: 'border-box',
      }}
    >
      {/* ID Column */}
      <Grid
        item
        xs="auto"
        sx={{ minWidth: isWorkbench ? '24px' : { xs: '30px', sm: '35px', md: '40px' } }}
      >
        <SortableHeader
          column="id"
          label="#"
          currentSortColumn={sortColumn}
          currentSortDirection={sortDirection}
          onClick={onSortChange}
          align="center"
          isMono
        />
      </Grid>

      {/* File Name / Name Column */}
      <Grid
        item
        xs={6}
        sm={4}
        md
        sx={{
          minWidth: isWorkbench ? 0 : { xs: '100px', sm: '140px', md: '180px' },
          flex: 1,
          overflow: 'hidden',
        }}
      >
        <SortableHeader
          column="fileName"
          label={variant === 'workbench' ? 'Name' : 'File Name'}
          currentSortColumn={sortColumn}
          currentSortDirection={sortDirection}
          onClick={onSortChange}
        />
      </Grid>

      {/* Speaker Column (Not sortable) */}
      {variant !== 'workbench' && (
        <Grid item xs={6} sm={3} md={2.5} sx={{ minWidth: { md: '90px' } }}>
          <Typography
            variant="subtitle1"
            fontWeight="bold"
            sx={{ fontSize: '13px', color: '#333' }}
          >
            Speaker
          </Typography>
        </Grid>
      )}

      {/* Start Time Column */}
      <Grid
        item
        xs={3}
        sm={2.5}
        md={2.5}
        sx={{ minWidth: isWorkbench ? '70px' : { md: '95px' }, flexShrink: 0 }}
      >
        <SortableHeader
          column="startTime"
          label={variant === 'workbench' ? 'Start' : 'Start Time'}
          currentSortColumn={sortColumn}
          currentSortDirection={sortDirection}
          onClick={onSortChange}
          align="center"
        />
      </Grid>

      {/* End Time Column */}
      <Grid
        item
        xs={3}
        sm={2.5}
        md={2.5}
        sx={{ minWidth: isWorkbench ? '70px' : { md: '95px' }, flexShrink: 0 }}
      >
        <SortableHeader
          column="endTime"
          label={variant === 'workbench' ? 'End' : 'End Time'}
          currentSortColumn={sortColumn}
          currentSortDirection={sortDirection}
          onClick={onSortChange}
          align="center"
        />
      </Grid>

      {variant !== 'workbench' && (
        <Grid item xs={12} sm={3} md={3}>
          {/* Empty space for Actions column */}
        </Grid>
      )}
    </Grid>
  );
};

export default ClipGridHeader;
